import React, { useCallback, useMemo, useState } from 'react';
import { Col, Row, Modal } from 'react-bootstrap';
import { Drawer, useMediaQuery } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as Funnel } from 'src/Images/icons/common/funnel.svg';
import { ReactComponent as BulletPoints } from 'src/Images/icons/common/bulletpoints.svg';
import { ReactComponent as ExcelIcon } from 'src/Images/icons/common/excel.svg';
import { ReactComponent as Refresh } from 'src/Images/icons/common/refresh.svg';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import { ReactComponent as ArrowLeft } from 'src/Images/icons/common/arrowleft.svg';
import { ReactComponent as ArrowRight } from 'src/Images/icons/common/arrowright.svg';
import { ReactComponent as Calendar } from 'src/Images/icons/common/calendar.svg';
import { ReactComponent as DropdownIcon } from 'src/Images/icons/common/dropdown.svg';
import { ReactComponent as Close } from 'src/Images/icons/common/close.svg';
import Table from 'src/components/Vehicle/List/Table';
import TableContent from 'src/components/VehicleTracking/List/TableContent';
import TableRightDrawer from 'src/components/Vehicle/List/TableRightDrawer';
import VehicleListMobileFilterDrawer from 'src/components/VehicleTracking/List/VehicleListMobileFilterDrawer';
import Breadcrumb from 'src/components/Breadcrumb';
import FilterMultiSelect from 'src/utils/FilterMultiSelect';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';
import { CustomToolTip } from 'src/utils/CustomToolTip';
import getColorCode from 'src/utils/GetThemeColor';
import usePageTitle from 'src/utils/SetPageTitle';
import { useSetBreadcrumbData } from 'src/utils/SetBreadcrumbData';
import {
  getVehicleTrackingList,
  getPlants,
  getAvailableStagesForWorkflowAssignedToUser,
} from 'src/redux/Actions/VehicleTracking';
import { loadExcelExportModules } from 'src/utils/excelExport';
import 'src/style/tableList.css';
import { useLocation, useNavigate } from 'react-router-dom';

const DEFAULT_FIELDS = ['1', '2', '3', '4', '5', '6', '7'];
const PREFERENCE_KEY = 'savedVehicleTrackingListColumns';

const directionOptions = [
  { value: '1', label: 'Inward' },
  { value: '2', label: 'Outward' },
];
const booleanOptions = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const today = new Date().toISOString().split('T')[0];

const emptyFilters = {
  direction: '',
  startDate: today,
  endDate: today,
  insuranceStartDate: null,
  insuranceEndDate: null,
  fitnessCertificateStartDate: null,
  fitnessCertificateEndDate: null,
  workflowStageId: '',
  plantId: '',
  completed: '',
  isOnHold: '',
};

export default function VehicleTrackingList() {
  usePageTitle('Vehicle Tracking');
  const location = useLocation();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width:767px)');
  const authReducer = useSelector((state) => state.AuthReducer);
  const vehicleTrackingList = useSelector(
    (state) => state.VehicleTrackingReducer.vehicleTrackingList,
  );
  const navigate = useNavigate();
  const { plantsData, assignedStagesForWorkflowAssignedToUser } = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const themeColor = authReducer?.userData?.tenant?.theme;
  const tenantOrganisationId = authReducer?.userData?.organisations?.id;
  const profileId = authReducer?.userData?.profile_id;

  const [pageNumber, setPageNumber] = useState(1);
  const [paginationBatchSize, setPaginationBatchSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showFunnel, setShowFunnel] = useState(false);
  const [openOrganizer, setOpenOrganizer] = useState(false);
  const [tableSortOrder, setTableSortOrder] = useState(null);
  const [tableSortField, setTableSortField] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [formData, setFormData] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

  const [
    selectVehicleTrackingForDetailNavigation,
    setSelectVehicleTrackingForDetailNavigation,
  ] = useState(null);
  const [showStageModal, setShowStageModal] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);
  const [stageError, setStageError] = useState(false);

  const handleNavigateButtonSubmit = () => {
    if (!selectedStage) {
      setStageError(true);
      return;
    }
    if (!selectVehicleTrackingForDetailNavigation) return;
    navigate(
      `/${selectedStage.value}/${selectVehicleTrackingForDetailNavigation.vehicle_tracking_unique_number}`,
    );
    handleCloseStageModal();
  };

  const savedPreferences = useMemo(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(PREFERENCE_KEY));
      return {
        selected: saved?.selected || DEFAULT_FIELDS,
        order: saved?.order || [],
      };
    } catch {
      return { selected: DEFAULT_FIELDS, order: [] };
    }
  }, []);
  const [selectedFieldFilter, setSelectedFieldFilter] = useState(
    savedPreferences.selected,
  );
  const [draggedFieldOrder, setDraggedFieldOrder] = useState(
    savedPreferences.order,
  );

  const fieldFilter = vehicleTrackingList?.fieldMapping || [];
  const vehicleTrackings = vehicleTrackingList?.vehicleTrackingsList || [];
  const totalPages = vehicleTrackingList?.totalPages || 1;
  const selectedFields = vehicleTrackingList?.selectedFields || [];
  const effectiveFieldOrder =
    draggedFieldOrder.length > 0 ? draggedFieldOrder : selectedFieldFilter;

  const tableHeaders = useMemo(
    () =>
      effectiveFieldOrder
        .map((fieldId) => {
          const selectedField = selectedFields.find(
            (field) => String(field.id) === String(fieldId),
          );
          return selectedField
            ? { ...selectedField, id: fieldId, columnId: fieldId }
            : null;
        })
        .filter(Boolean),
    [effectiveFieldOrder, selectedFields],
  );

  useSetBreadcrumbData(location.pathname, 'Vehicle Tracking');

  const debouncedSearch = useCallback(() => {
    const timer = setTimeout(() => {
      setSearchInput(searchText);
      setPageNumber(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  React.useEffect(debouncedSearch, [debouncedSearch]);

  const fetchVehicleTrackings = useCallback(() => {
    if (!tenantOrganisationId) return;
    const params = new URLSearchParams();
    if (appliedFilters.startDate) {
      params.append('start_date', appliedFilters.startDate);
    }
    if (appliedFilters.endDate) {
      params.append('end_date', appliedFilters.endDate);
    }
    if (appliedFilters.direction) {
      params.append('directions', appliedFilters.direction);
    }
    if (appliedFilters.workflowStageId) {
      params.append('workflow_stage_id', appliedFilters.workflowStageId);
    }
    if (appliedFilters.plantId) {
      params.append('plant_id', appliedFilters.plantId);
    }
    if (appliedFilters.insuranceStartDate) {
      params.append('insurance_start_date', appliedFilters.insuranceStartDate);
    }
    if (appliedFilters.insuranceEndDate) {
      params.append('insurance_end_date', appliedFilters.insuranceEndDate);
    }
    if (appliedFilters.fitnessCertificateStartDate) {
      params.append(
        'fitness_certificate_start_date',
        appliedFilters.fitnessCertificateStartDate,
      );
    }
    if (appliedFilters.fitnessCertificateEndDate) {
      params.append(
        'fitness_certificate_end_date',
        appliedFilters.fitnessCertificateEndDate,
      );
    }
    if (appliedFilters.completed !== '') {
      params.append('completed', appliedFilters.completed);
    }
    if (appliedFilters.isOnHold !== '') {
      params.append('is_on_hold', appliedFilters.isOnHold);
    }

    dispatch(
      getVehicleTrackingList(
        tenantOrganisationId,
        pageNumber,
        searchInput,
        tableSortOrder,
        tableSortField,
        effectiveFieldOrder,
        paginationBatchSize,
        params.toString(),
      ),
    );
  }, [
    appliedFilters,
    dispatch,
    effectiveFieldOrder,
    pageNumber,
    paginationBatchSize,
    refreshKey,
    searchInput,
    tableSortField,
    tableSortOrder,
    tenantOrganisationId,
  ]);

  React.useEffect(() => {
    fetchVehicleTrackings();
  }, [fetchVehicleTrackings]);

  React.useEffect(() => {
    localStorage.setItem(
      PREFERENCE_KEY,
      JSON.stringify({
        selected: selectedFieldFilter,
        order: draggedFieldOrder,
      }),
    );
  }, [draggedFieldOrder, selectedFieldFilter]);

  const toggleOrganizer = (open) => () => setOpenOrganizer(open);

  const handleSelectedFieldFilter = (event, value) => {
    setSelectedFieldFilter((current) =>
      event.target.checked
        ? [...new Set([...current, value])]
        : current.filter((field) => field !== value),
    );
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleResetFilter = () => {
    setFormData(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPageNumber(1);
  };

  const handleApplyFilter = () => {
    setAppliedFilters(formData);
    setPageNumber(1);
    if (isMobile) setShowFunnel(false);
  };

  const handleTableSortOrder = (value, field) => {
    setTableSortOrder(value);
    setTableSortField(field);
    setPageNumber(1);
  };

  const exportToExcel = async () => {
    if (!vehicleTrackings.length) return;
    const { utils, write, saveAs } = await loadExcelExportModules();
    const rows = vehicleTrackings.map((vehicle) => {
      const row = {};
      tableHeaders.forEach((header) => {
        const cellValue = vehicle[header.value];
        if (Array.isArray(cellValue)) {
          row[header.label] = cellValue.length ? cellValue.join(', ') : '-';
        } else if (cellValue && typeof cellValue === 'object') {
          row[header.label] = cellValue.name ?? '-';
        } else if (typeof cellValue === 'boolean') {
          row[header.label] = cellValue ? 'Yes' : 'No';
        } else {
          row[header.label] = cellValue ?? '-';
        }
      });
      return row;
    });
    const worksheet = utils.json_to_sheet(rows);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, 'VehicleTracking');
    const excelBuffer = write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([excelBuffer], { type: 'application/octet-stream' }),
      `VehicleTracking_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  const handleInputChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value) && value >= 1 && value <= totalPages) {
      setPageNumber(value);
    }
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const handleNextPage = () => {
    if (pageNumber < totalPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const formattedPageNumber = pageNumber < 10 ? `0${pageNumber}` : pageNumber;

  const workflowStageOptions =
    assignedStagesForWorkflowAssignedToUser?.map((stageObj) => ({
      value: stageObj.id,
      label: stageObj.name,
    })) || [];
  const plantOptions =
    plantsData?.map((plantObj) => ({
      value: plantObj.id,
      label: plantObj.name,
    })) || [];

  const workflowStageValue =
    workflowStageOptions.find(
      (option) => String(option.value) === String(formData.workflowStageId),
    ) || '';
  const plantValue =
    plantOptions.find(
      (option) => String(option.value) === String(formData.plantId),
    ) || '';

  const hasUnappliedFilters = () =>
    Object.keys(formData).some((key) => formData[key] !== appliedFilters[key]);

  // --- Stage change modal handlers ---
  const handleOpenStageModal = (vehicleTracking) => {
    setSelectVehicleTrackingForDetailNavigation(vehicleTracking);
    setSelectedStage(null);
    setStageError(false);
    setShowStageModal(true);
  };

  const handleCloseStageModal = () => {
    setShowStageModal(false);
    setSelectVehicleTrackingForDetailNavigation(null);
    setSelectedStage(null);
    setStageError(false);
  };

  const stageSelectOptions =
    assignedStagesForWorkflowAssignedToUser?.map((stage) => ({
      value: stage.id,
      label: stage.name,
    })) || [];

  return (
    <div className="div-flex-column" data-testid="vehicles-page">
      <div className="page-layout div-flex-column">
        <div className="div-space-between slider-padding left-padding">
          <div className="div-flex-row cg-5">
            <Breadcrumb />
          </div>
          <Col xxl={2} xl={2} lg={4} md={5} sm={7} xs={7} className="p-0 m-0">
            <div className="input-wrapper">
              <input
                type="text"
                data-testid="vehicles-search-input"
                className="form-input-section input-icon-remove"
                placeholder="Search vehicle tracking"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
              <span className="form-input-section-icon">
                <Search
                  className="icon-size-lg"
                  color={getColorCode(themeColor)}
                />
              </span>
            </div>
          </Col>
        </div>
        <div className="div-space-between border-bottom slider-padding">
          <span className="text-xxl text-bold left-padding">
            Vehicle Tracking
          </span>
          <div className="div-flex-row div-align-center cg-20">
            <div className="div-flex-row cg-20 div-align-center">
              <div className="list-container div-flex-column rg-5">
                <CustomToolTip title="Filter" placement="bottom">
                  <button
                    data-testid="filter-open-btn"
                    className="icon-btn-none"
                    aria-label="filter"
                    onClick={() => {
                      setShowFunnel((current) => !current);
                      if (isMobile) setShowFunnel(true);
                    }}
                  >
                    <Funnel
                      className="icon-size-lg"
                      color={showFunnel ? getColorCode(themeColor) : '#9F9F9F'}
                    />
                  </button>
                </CustomToolTip>
              </div>
              <div className="button-container">
                <CustomToolTip title="Organiser" placement="bottom">
                  <button
                    data-testid="organizer-open-btn"
                    className="icon-btn-none"
                    aria-label="organizer"
                    onClick={toggleOrganizer(true)}
                  >
                    <BulletPoints className="icon-size-lg" color="#9F9F9F" />
                  </button>
                </CustomToolTip>
              </div>
              <Drawer
                anchor="right"
                open={openOrganizer}
                onClose={toggleOrganizer(false)}
                ModalProps={{
                  keepMounted: false,
                }}
                sx={{ zIndex: 2 }}
              >
                <div className="right-drawer-component-small">
                  <TableRightDrawer
                    toggleDrawer={toggleOrganizer}
                    fieldFilter={fieldFilter}
                    selectedFieldFilter={selectedFieldFilter}
                    fieldOrder={effectiveFieldOrder}
                    setDraggedFieldOrder={setDraggedFieldOrder}
                    handleSelectedFieldFilter={handleSelectedFieldFilter}
                  />
                </div>
              </Drawer>
              <div className="button-container">
                <CustomToolTip title="Export Excel" placement="bottom">
                  <button
                    aria-label="export excel"
                    className="icon-btn-none"
                    onClick={exportToExcel}
                  >
                    <ExcelIcon className="icon-size-lg" color="#9F9F9F" />
                  </button>
                </CustomToolTip>
              </div>
              {!isMobile && (
                <CustomToolTip title="Refresh" placement="bottom">
                  <button
                    aria-label="refresh"
                    className="icon-btn-none"
                    onClick={() => setRefreshKey((current) => current + 1)}
                  >
                    <Refresh className="icon-size-lg" color="#9F9F9F" />
                  </button>
                </CustomToolTip>
              )}
            </div>
            {!isMobile && (
              <select
                value={paginationBatchSize}
                onChange={(event) => {
                  setPaginationBatchSize(Number(event.target.value));
                  setPageNumber(1);
                }}
                className="filter-input-section"
              >
                {[10, 20, 30, 40, 50, 100, 500, 1000].map((size) => (
                  <option value={size} key={size}>
                    {size}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
        <Row className="g-0">
          <Col
            xxl={showFunnel ? 10 : 12}
            xl={showFunnel ? 9 : 12}
            lg={showFunnel ? 8 : 12}
            md={showFunnel ? 8 : 12}
            sm={showFunnel ? 0 : 12}
            xs={showFunnel ? 0 : 12}
            className="border-bottom"
          >
            <Table
              headers={tableHeaders}
              minCellWidth={150}
              tableContent={
                <TableContent
                  headers={tableHeaders}
                  vehicleTrackings={vehicleTrackings}
                  onVehicleCodeClick={handleOpenStageModal}
                />
              }
              tableSortOrder={tableSortOrder}
              handleTableSortOrder={handleTableSortOrder}
            />
            <div className="footer-container">
              <div className="div-space-between div-align-center footer-content-for-page">
                <div className="div-flex-row cg-20 pagination-container div-align-center">
                  <div className="div-flex-row cg-10 div-align-center">
                    <button
                      data-testid="pagination-prev"
                      className="icon-btn-none"
                      onClick={handlePreviousPage}
                      aria-label="previous page"
                    >
                      <ArrowLeft
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                      />
                    </button>
                    <input
                      className="page-number-container input-icon-remove text-sm"
                      data-testid="pagination-input"
                      value={formattedPageNumber}
                      onChange={handleInputChange}
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      max={totalPages}
                    />
                    <button
                      data-testid="pagination-next"
                      className="icon-btn-none"
                      onClick={handleNextPage}
                      aria-label="next page"
                    >
                      <ArrowRight
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                      />
                    </button>
                  </div>
                  <span className="text-grey text-sm">
                    Page {pageNumber} of {totalPages}
                  </span>
                </div>
              </div>
            </div>
          </Col>
          {showFunnel && !isMobile && (
            <Col
              xxl={showFunnel ? 2 : 0}
              xl={showFunnel ? 3 : 0}
              lg={showFunnel ? 4 : 0}
              md={showFunnel ? 4 : 0}
              sm={showFunnel ? 12 : 0}
              xs={showFunnel ? 12 : 0}
              className="border-left border-bottom"
            >
              <div
                className="filter-list-item div-flex-row div-space-between div-align-center border-bottom"
                data-testid="filter-drawer"
              >
                <span className="text-sm text-semi-bold">Filter</span>
                {hasUnappliedFilters() && (
                  <div className="text-yellow text-xs text-semi-bold">
                    ⚠️ Unapplied Filters
                  </div>
                )}
              </div>
              <div className="div-flex-column div-space-between rg-20">
                <div className="filter-list-item-container-scroll div-flex-column">
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">Direction</span>
                    <select
                      name="direction"
                      value={formData.direction || ''}
                      onChange={handleFilterChange}
                      className="form-input-section"
                    >
                      <option value="">Select Direction</option>
                      {directionOptions.map((option) => (
                        <option value={option.value} key={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Workflow Stage
                    </span>
                    <div className="input-wrapper">
                      <FilterMultiSelect
                        isMulti={false}
                        name="workflowStageId"
                        className="multi-select-input-section"
                        value={workflowStageValue}
                        options={workflowStageOptions}
                        classNamePrefix="react-select"
                        placeholder="Select Stage"
                        onFocus={() =>
                          dispatch(
                            getAvailableStagesForWorkflowAssignedToUser(
                              tenantOrganisationId,
                              '',
                              formData.direction || null,
                            ),
                          )
                        }
                        onChange={(selectedOption) =>
                          setFormData((current) => ({
                            ...current,
                            workflowStageId: selectedOption?.value || '',
                          }))
                        }
                      />
                      <span className="form-input-section-icon">
                        <ArrowRight
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                          style={{ transform: 'rotate(90deg)' }}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">Plant</span>
                    <div className="input-wrapper">
                      <FilterMultiSelect
                        isMulti={false}
                        name="plantId"
                        className="multi-select-input-section"
                        value={plantValue}
                        options={plantOptions}
                        classNamePrefix="react-select"
                        placeholder="Select Plant"
                        onFocus={() =>
                          dispatch(getPlants(tenantOrganisationId))
                        }
                        onChange={(selectedOption) =>
                          setFormData((current) => ({
                            ...current,
                            plantId: selectedOption?.value || '',
                          }))
                        }
                      />
                      <span className="form-input-section-icon">
                        <ArrowRight
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                          style={{ transform: 'rotate(90deg)' }}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">Start Date</span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="startDate"
                        value={formData.startDate || ''}
                        onChange={handleFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">End Date</span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="endDate"
                        value={formData.endDate || ''}
                        onChange={handleFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Insurance Start Date
                    </span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="insuranceStartDate"
                        value={formData.insuranceStartDate || ''}
                        onChange={handleFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Insurance End Date
                    </span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="insuranceEndDate"
                        value={formData.insuranceEndDate || ''}
                        onChange={handleFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Fitness Certificate Start Date
                    </span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="fitnessCertificateStartDate"
                        value={formData.fitnessCertificateStartDate || ''}
                        onChange={handleFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Fitness Certificate End Date
                    </span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="fitnessCertificateEndDate"
                        value={formData.fitnessCertificateEndDate || ''}
                        onChange={handleFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">Completed</span>
                    <select
                      name="completed"
                      value={formData.completed}
                      onChange={handleFilterChange}
                      className="form-input-section"
                    >
                      <option value="">Select</option>
                      {booleanOptions.map((option) => (
                        <option value={option.value} key={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">On Hold</span>
                    <select
                      name="isOnHold"
                      value={formData.isOnHold}
                      onChange={handleFilterChange}
                      className="form-input-section"
                    >
                      <option value="">Select</option>
                      {booleanOptions.map((option) => (
                        <option value={option.value} key={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="div-flex-row div-space-between div-align-center border-top filter-list-item footer-content-for-component">
                  <button
                    className={`common-btn-outlined border-${themeColor}`}
                    onClick={handleResetFilter}
                    data-testid="filter-reset-btn"
                  >
                    Reset
                  </button>
                  <button
                    className={`common-btn theme-background-${themeColor}`}
                    onClick={handleApplyFilter}
                    data-testid="filter-apply-btn"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </Col>
          )}
        </Row>
        <Drawer
          anchor="right"
          open={isMobile && showFunnel}
          onClose={() => setShowFunnel(false)}
          ModalProps={{
            keepMounted: false,
          }}
          sx={{ zIndex: 2 }}
        >
          <div className="right-drawer-component-small">
            <VehicleListMobileFilterDrawer
              formData={formData}
              handleSearchInputChange={handleFilterChange}
              handleResetFilter={handleResetFilter}
              handleApplyFilter={handleApplyFilter}
              closeFilter={() => setShowFunnel(false)}
              workflowStageOptions={workflowStageOptions}
              plantOptions={plantOptions}
              workflowStageValue={workflowStageValue}
              plantValue={plantValue}
              tenantOrganisationId={tenantOrganisationId}
              profileId={profileId}
            />
          </div>
        </Drawer>

        {/* Stage Selection Modal */}
        <Modal
          show={showStageModal}
          onHide={handleCloseStageModal}
          centered
          size="md"
          dialogClassName="custom-modal"
        >
          <Modal.Body>
            <div className="div-flex-column rg-20">
              <Row>
                <Col xl={11} lg={11} md={11} sm={11} xs={11}>
                  <div className="div-flex-row cg-10 div-space-between">
                    <span className="text-xl text-nowrap">
                      Select Stage To Navigate{' '}
                      {selectVehicleTrackingForDetailNavigation
                        ? ` — ${selectVehicleTrackingForDetailNavigation.vehicle_tracking_code}`
                        : ''}
                    </span>
                  </div>
                </Col>
                <Col xl={1} lg={1} md={1} sm={1} xs={1}>
                  <div className="div-flex-row div-flex-end">
                    <Close
                      className="icon-size-sm cursor-pointer"
                      onClick={handleCloseStageModal}
                    />
                  </div>
                </Col>
              </Row>

              <div className="div-flex-column rg-5">
                <span className="text-sm text-semi-bold">Assigned Stages</span>
                <div className="input-wrapper">
                  <CustomMultiSelect
                    isMulti={false}
                    options={stageSelectOptions}
                    value={selectedStage}
                    className={`multi-select-input-section ${
                      stageError ? 'form-input-error' : ''
                    }`}
                    onChange={(selectedOption) => {
                      setSelectedStage(selectedOption);
                      setStageError(false);
                    }}
                    onFocus={() => {
                      if (tenantOrganisationId) {
                        const workflowId =
                          selectVehicleTrackingForDetailNavigation?.workflow
                            ?.id;
                        const vehicleDirection =
                          selectVehicleTrackingForDetailNavigation?.direction
                            ?.id;
                        dispatch(
                          getAvailableStagesForWorkflowAssignedToUser(
                            tenantOrganisationId,
                            workflowId,
                            vehicleDirection,
                          ),
                        );
                      }
                    }}
                    placeholder="Select a stage..."
                    isSearchable
                    classNamePrefix="react-select"
                  />
                  <span className="form-input-section-icon">
                    <DropdownIcon
                      className="icon-size-sm"
                      color={getColorCode(themeColor)}
                    />
                  </span>
                </div>
              </div>
              <div className="div-flex-row div-space-between">
                <button
                  className={`d-flex vehicle-tracking-detail-add-vehcile-data-btn theme-text-${themeColor} theme-border-color-${themeColor} div-align-center`}
                  onClick={handleCloseStageModal}
                >
                  Cancel
                </button>
                <button
                  className={`d-flex vehicle-tracking-detail-move-stage-btn theme-background-${themeColor}`}
                  onClick={handleNavigateButtonSubmit}
                >
                  View
                </button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
}
